import React, { useState, useEffect } from 'react';
import { Archive, Trash2, MoreVertical, Printer, Pencil, RotateCcw, CheckCircle2, Calendar, Building2 } from 'lucide-react';
import { useProperties } from '../../hooks/useProperties';
import { useAuth } from '../../contexts/AuthContext';
import { useAgencies } from '../../hooks/useAgencies';
import { Button } from '../ui/Button';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { PropertyStatus } from '../../types/property';
import {
  generateShowcasePDF,
  generateClientPDF,
  generatePrivatePDF,
  generateContractPDF,
  generateVisitVoucherPDF
} from '../../utils/pdfGenerator';
import PrintMenu from './forms/tabs/Photos/PrintMenu';
import { useNavigate } from 'react-router-dom';
import { Agency } from '../../types/agency';
import { VisitVoucherData } from './forms/tabs/Photos/VisitVoucherModal';

interface PropertyActionsProps {
  propertyId: string;
  currentStatus: PropertyStatus;
  onActionComplete?: () => void;
}

type ConfirmDialogType = 'delete' | 'archive' | 'sold' | 'rented' | 'reactivate' | 'putForSale' | null;

export const PropertyActions: React.FC<PropertyActionsProps> = ({
  propertyId,
  currentStatus,
  onActionComplete,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showPrintMenu, setShowPrintMenu] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogType>(null);
  const [userAgency, setUserAgency] = useState<Agency | null>(null);
  const { archiveProperty, deleteProperty, getPropertyById, updateProperty } = useProperties();
  const { user } = useAuth();
  const { agencies } = useAgencies();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.agency_id && agencies.length > 0) {
      const agency = agencies.find(a => a.id === user.agency_id.toString());
      if (agency) {
        setUserAgency(agency);
      }
    }
  }, [user, agencies]);

  const handleEdit = () => {
    console.log('✏️ Modification du bien:', propertyId);
    navigate(`/modifier/${propertyId}`);
    setShowMenu(false);
  };

  const handleArchive = async () => {
    console.log('📦 Archivage du bien:', propertyId);
    const success = await archiveProperty(propertyId);
    if (success) {
      setShowMenu(false);
      onActionComplete?.();
      // Forcer un refresh de la page pour afficher la liste mise à jour
      navigate(0);
    }
  };

  const handleDelete = async () => {
    console.log('🗑️ Suppression du bien:', propertyId);
    const success = await deleteProperty(propertyId);
    if (success) {
      setShowMenu(false);
      onActionComplete?.();
      // Forcer un refresh de la page pour afficher la liste mise à jour
      navigate(0);
    }
  };

  const handleMarkAsSold = async () => {
    console.log('✅ Marquer comme vendu:', propertyId);
    const success = await updateProperty(parseInt(propertyId), { status: 'sold' });
    if (success) {
      setShowMenu(false);
      onActionComplete?.();
      // Forcer un refresh de la page pour afficher la liste mise à jour
      navigate(0);
    }
  };

  const handleMarkAsRented = async () => {
    console.log('🏠 Marquer comme loué:', propertyId);
    const success = await updateProperty(parseInt(propertyId), { status: 'rented' });
    if (success) {
      setShowMenu(false);
      onActionComplete?.();
      // Forcer un refresh de la page pour afficher la liste mise à jour
      navigate(0);
    }
  };

  const handleReactivate = async () => {
    console.log('🔄 Réactivation du bien:', propertyId);
    const originalStatus = currentStatus === 'sold' ? 'for-sale' : 'for-rent';
    const success = await updateProperty(parseInt(propertyId), { status: originalStatus });
    if (success) {
      setShowMenu(false);
      onActionComplete?.();
      // Forcer un refresh de la page pour afficher la liste mise à jour
      navigate(0);
    }
  };

  const handlePutForSale = async () => {
    console.log('🏷️ Mise en vente du bien:', propertyId);
    const success = await updateProperty(parseInt(propertyId), { status: 'for-sale' });
    if (success) {
      setShowMenu(false);
      onActionComplete?.();
      // Forcer un refresh de la page pour afficher la liste mise à jour
      navigate(0);
    }
  };

  const getConfirmDialogProps = () => {
    switch (confirmDialog) {
      case 'delete':
        return {
          title: 'Supprimer le bien',
          message: 'Êtes-vous sûr de vouloir supprimer ce bien ? Cette action est irréversible.',
          confirmText: 'Supprimer',
          variant: 'danger' as const,
          onConfirm: handleDelete,
        };
      case 'archive':
        return {
          title: 'Archiver le bien',
          message: 'Êtes-vous sûr de vouloir archiver ce bien ?',
          confirmText: 'Archiver',
          variant: 'warning' as const,
          onConfirm: handleArchive,
        };
      case 'sold':
        return {
          title: 'Marquer comme vendu',
          message: 'Êtes-vous sûr de vouloir marquer ce bien comme vendu ?',
          confirmText: 'Confirmer',
          variant: 'info' as const,
          onConfirm: handleMarkAsSold,
        };
      case 'rented':
        return {
          title: 'Marquer comme loué',
          message: 'Êtes-vous sûr de vouloir marquer ce bien comme loué ?',
          confirmText: 'Confirmer',
          variant: 'info' as const,
          onConfirm: handleMarkAsRented,
        };
      case 'reactivate':
        return {
          title: 'Réactiver le bien',
          message: 'Êtes-vous sûr de vouloir réactiver ce bien ?',
          confirmText: 'Réactiver',
          variant: 'info' as const,
          onConfirm: handleReactivate,
        };
      case 'putForSale':
        return {
          title: currentStatus === 'estimation' ? 'Mettre en vente' : 'Remettre en vente',
          message: currentStatus === 'estimation'
            ? 'Êtes-vous sûr de vouloir mettre cette estimation en vente ?'
            : 'Êtes-vous sûr de vouloir remettre ce bien en vente ?',
          confirmText: 'Confirmer',
          variant: 'info' as const,
          onConfirm: handlePutForSale,
        };
      default:
        return null;
    }
  };

  const handlePrint = async (
    type: 'showcase' | 'client' | 'private' | 'contract' | 'visit-voucher', 
    orientation?: 'portrait' | 'landscape',
    visitData?: VisitVoucherData
  ) => {
    const property = getPropertyById(propertyId);

    console.log('🖨️ handlePrint appelé avec:', { type, orientation, propertyId, visitData });
    console.log('🏠 Property trouvée:', property);

    if (!property) return;

    const baseUrl = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001').replace('/api', '');
    const logoUrl = userAgency?.logo_url
      ? (userAgency.logo_url.startsWith('http')
          ? userAgency.logo_url
          : `${baseUrl}${userAgency.logo_url}`)
      : undefined;

    console.log('🖼️ Logo URL construite:', {
      baseUrl,
      logoUrlFromDB: userAgency?.logo_url,
      finalLogoUrl: logoUrl
    });

    try {
      console.log('🚀 Début génération PDF:', type, 'pour le bien:', property.id);
      console.log('📋 Données de la propriété:', {
        id: property.id,
        type: property.type,
        address: property.address,
        price: property.price,
        photos: property.photos
      });

      switch (type) {
        case 'showcase':
          console.log('📄 Génération showcase PDF...');
          await generateShowcasePDF(
            property,
            {
              name: userAgency?.name || 'Agence Immobilière',
              contact: `${userAgency?.phone || ''} • ${userAgency?.email || ''}`,
              logo: logoUrl,
              brandColor: userAgency?.brand_color,
            },
            { orientation: orientation || 'portrait' }
          );
          console.log('✅ Showcase PDF généré');
          break;
        case 'client':
          console.log('📄 Génération client PDF...');
          await generateClientPDF(property, userAgency?.brand_color);
          console.log('✅ Client PDF généré');
          break;
        case 'private':
          console.log('📄 Génération private PDF...');
          await generatePrivatePDF(property, userAgency?.brand_color);
          console.log('✅ Private PDF généré');
          break;
        case 'contract':
          console.log('📄 Génération contract PDF...');
          await generateContractPDF(property, {
            name: userAgency?.name || 'Agence Immobilière',
            address: `${userAgency?.address || ''}, ${userAgency?.city || ''}`,
            brandColor: userAgency?.brand_color,
          });
          console.log('✅ Contract PDF généré');
          break;
        case 'visit-voucher':
          console.log('📄 Génération visit voucher PDF avec données:', visitData);
          await generateVisitVoucherPDF(property, {
            name: userAgency?.name || 'Agence Immobilière',
            address: `${userAgency?.address || ''}, ${userAgency?.city || ''}`,
            phone: userAgency?.phone || '',
            brandColor: userAgency?.brand_color,
          }, visitData);
          console.log('✅ Visit voucher PDF généré');
          break;
      }
      console.log('🎉 PDF généré avec succès pour le type:', type);
    } catch (error) {
      console.error('❌ Erreur lors de la génération PDF:', error);
      console.error('📋 Stack trace:', error instanceof Error ? error.stack : 'Pas de stack trace');
      console.error('📦 Type d\'erreur:', typeof error);
      console.error('📊 Détails de l\'erreur:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      alert(`Une erreur est survenue lors de la génération du PDF: ${errorMessage}`);
    }
  };

  return (
    <>
      <div className="relative">
        <Button
          variant="secondary"
          size="icon"
          onClick={(e) => {
            e.stopPropagation();
            setShowMenu(!showMenu);
          }}
          className="!p-2"
        >
          <MoreVertical className="h-4 w-4" />
        </Button>

        {showMenu && (
          <div 
            className="absolute right-0 top-full z-10 mt-1 w-48 rounded-lg bg-white py-1 shadow-lg ring-1 ring-black/5 dark:bg-gray-800 dark:ring-white/5"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                console.log('🖨️ Bouton Imprimer cliqué');
                setShowPrintMenu(true);
                setShowMenu(false);
              }}
              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              <Printer className="h-4 w-4" />
              Imprimer
            </button>
            {(currentStatus === 'for-sale' || currentStatus === 'estimation') && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmDialog('sold');
                  setShowMenu(false);
                }}
                className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                Passer en vendu
              </button>
            )}
            {currentStatus === 'estimation' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmDialog('putForSale');
                  setShowMenu(false);
                }}
                className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                <Building2 className="h-4 w-4 text-blue-500" />
                Mettre en vente
              </button>
            )}
            {(currentStatus === 'for-rent' || currentStatus === 'for-short-rent') && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmDialog('rented');
                  setShowMenu(false);
                }}
                className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                <CheckCircle2 className="h-4 w-4 text-blue-500" />
                Marquer comme loué
              </button>
            )}
            {(currentStatus === 'sold' || currentStatus === 'rented') && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmDialog('reactivate');
                  setShowMenu(false);
                }}
                className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                <RotateCcw className="h-4 w-4 text-blue-500" />
                Réactiver
              </button>
            )}
            {currentStatus === 'archived' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmDialog('putForSale');
                  setShowMenu(false);
                }}
                className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                <Building2 className="h-4 w-4 text-blue-500" />
                Remettre en vente
              </button>
            )}
            {currentStatus !== 'archived' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmDialog('archive');
                  setShowMenu(false);
                }}
                className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                <Archive className="h-4 w-4" />
                Archiver
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setConfirmDialog('delete');
                setShowMenu(false);
              }}
              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-gray-100 dark:text-red-400 dark:hover:bg-gray-700"
            >
              <Trash2 className="h-4 w-4" />
              Supprimer
            </button>
          </div>
        )}
      </div>

      <PrintMenu
        isOpen={showPrintMenu}
        onClose={() => {
          setShowPrintMenu(false);
        }}
        onPrint={handlePrint}
      />

      {confirmDialog && (
        <ConfirmDialog
          isOpen={true}
          onClose={() => setConfirmDialog(null)}
          {...getConfirmDialogProps()!}
        />
      )}
    </>
  );
};